import type { AppDispatch } from "../redux/store";
import { clearCart } from "../redux/slices/cartSlice";
import { clearFavourites } from "../redux/slices/favouritesSlice";
import { resetQRScanState } from "../redux/slices/qrScanSlice";
import { clearPresetStation, setPresetStation } from "../redux/slices/stationSlice";
import { resetFlow } from "../redux/slices/uploadFlowSlice";
import { getActiveAiSelfieArtsByCategory, type AiSelfieArt } from "../services/aiSelfieArtService";
import {
  clearAiSelfieCaricatureHobbyPostPayment,
  clearAiSelfieCaricatureHobbyPrompt,
  isCaricatureHobbyAiSelfieArt,
  isFifaAiSelfieArt,
  readSelectedAiSelfieArtFromStorage,
} from "../services/stylePromptResolver";
import { SELECTED_FIFA_CATEGORY_STORAGE_KEY } from "../services/fifaCategoryService";
import { SELECTED_FIFA_TEAM_STORAGE_KEY } from "../services/fifaTeamService";
import {
  clearHobbyCharacterNologoMap,
  HOBBY_GENERATION_STORAGE_KEY,
} from "./hobbyGenerationNavigation";
import { clearPendingPaymentOrder } from "./pendingPaymentOrder";
import { clearStoredAuthUser } from "./authSession";
import { clearMerchGenerateCache } from "../services/merchGenerateService";

export {
  resolveFifaStationSlug,
} from "../services/stylePromptResolver";

export const FIFA_WORLD_CUP_2026_NAME = "FIFA World Cup 2026";
export const SELECTED_CARICATURE_HOBBIES_STORAGE_KEY = "selectedCaricatureHobbies";
export const SELECTED_FIFA_CARD_DESIGN_KEY = "selectedFifaCardDesign";
export const FIFA_PLAYER_PROFILE_KEY = "fifaPlayerProfile";
export const FIFA_PLAYER_STATS_KEY = "fifaPlayerStats";
export const FIFA_SELFIE_KEY = "fifaSelfieImage";
export const FIFA_GENERATED_CARD_KEY = "fifaGeneratedCard";
export const FIFA_CARD_SAVE_REQUESTED_KEY = "fifaCardSaveRequested";
export const FIFA_CARD_TRY_AGAIN_USED_KEY = "fifaCardTryAgainUsed";
export const TRANSFORMATION_TRY_AGAIN_USED_KEY = "transformationTryAgainUsed";

export type FifaPlayerProfile = {
  clubTeam: string;
  firstName: string;
  lastName: string;
  jerseyNumber: string;
};

export type FifaStatKey = "pac" | "sho" | "pas" | "dri" | "def" | "phy";

export type FifaPlayerStats = {
  position: string;
  rating: string;
  pac: number;
  sho: number;
  pas: number;
  dri: number;
  def: number;
  phy: number;
};

export type FifaCardDesignId = string;

export type StoredStationRecord = {
  id?: string | number;
  name?: string;
  type?: string;
  href?: string;
  imgURL?: string;
  backgroundImageUrl?: string;
  inner_bg_image?: string | null;
  background_image?: string | null;
  isFifaWorldCup2026?: boolean;
};

export function isFifaWorldCup2026Station(
  station?: { name?: string; isFifaWorldCup2026?: boolean } | null,
  selectedArt?: { is_fifa?: unknown } | null
): boolean {
  if (station?.isFifaWorldCup2026 === true) return true;
  if (station?.name?.trim() === FIFA_WORLD_CUP_2026_NAME) return true;

  const art = selectedArt !== undefined ? selectedArt : readSelectedAiSelfieArtFromStorage();
  return isFifaAiSelfieArt(art);
}

export function readStoredStationData(): StoredStationRecord | null {
  try {
    const raw = localStorage.getItem("stationData");
    return raw ? (JSON.parse(raw) as StoredStationRecord) : null;
  } catch {
    return null;
  }
}

export function readSelectedStation(): StoredStationRecord | null {
  try {
    const raw = localStorage.getItem("selectedStation");
    return raw ? (JSON.parse(raw) as StoredStationRecord) : null;
  } catch {
    return null;
  }
}

export function readActiveStoredStation(): StoredStationRecord | null {
  return readSelectedStation() || readStoredStationData();
}

export function slugifyRouteValue(value?: string | null): string {
  return (value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Category slug for landing pages like `/:slug/all`. */
export function resolveStationCategorySlug(
  station?: StoredStationRecord | null,
  routeSlug?: string | null
): string {
  return (
    station?.type?.trim() ||
    readActiveStoredStation()?.type?.trim() ||
    routeSlug?.trim() ||
    "station"
  );
}

/**
 * Inner-flow route slug after `/:category/all`.
 * Prefer slugified selectedAiSelfieArt.name, then current route slug, then category.
 */
export function resolveStationRouteSlug(
  station?: StoredStationRecord | null,
  routeSlug?: string | null,
  art?: { name?: string | null } | null
): string {
  const selectedArt = art ?? readSelectedAiSelfieArtFromStorage();
  const fromArt = slugifyRouteValue(selectedArt?.name);
  if (fromArt) return fromArt;

  const fromRoute = routeSlug?.trim() || "";
  if (fromRoute) return fromRoute;

  return resolveStationCategorySlug(station, routeSlug);
}

/** Keep theme backgrounds on station so payment pages still resolve after art is cleared. */
export function enrichStationWithThemeBackgrounds(
  station?: StoredStationRecord | null,
  art?: {
    inner_bg_image?: string | null;
    background_image?: string | null;
    home_bg_image?: string | null;
    category?: {
      inner_bg_image?: string | null;
      background_image?: string | null;
    } | null;
  } | null
): StoredStationRecord {
  const base = { ...(station || readActiveStoredStation() || {}) };
  const selectedArt = art ?? readSelectedAiSelfieArtFromStorage();
  const artInner =
    selectedArt?.inner_bg_image?.trim() ||
    selectedArt?.category?.inner_bg_image?.trim() ||
    "";
  const artBackground =
    selectedArt?.background_image?.trim() ||
    selectedArt?.category?.background_image?.trim() ||
    selectedArt?.home_bg_image?.trim() ||
    "";

  // When art provides theme images, always overwrite station cache (don't keep prior art BG).
  if (artInner) {
    base.inner_bg_image = artInner;
  }
  if (artBackground) {
    base.background_image = artBackground;
    base.backgroundImageUrl = artBackground;
  } else if (!base.background_image?.trim() && base.backgroundImageUrl?.trim()) {
    base.background_image = base.backgroundImageUrl;
  }

  return base;
}

export function enrichStationRecord<T extends StoredStationRecord>(
  station: T
): T & { isFifaWorldCup2026: boolean } {
  return {
    ...station,
    isFifaWorldCup2026: isFifaWorldCup2026Station(station),
  };
}

export function persistStationSelection(station: StoredStationRecord): StoredStationRecord {
  const existing = readActiveStoredStation();
  const merged: StoredStationRecord = {
    ...(existing || {}),
    ...(station || {}),
  };

  // Never overwrite a valid category slug with an empty station payload
  if (!merged.type?.trim() && existing?.type?.trim()) {
    merged.type = existing.type;
  }
  if (
    (merged.id === undefined || merged.id === null || String(merged.id).trim() === "") &&
    existing?.id !== undefined &&
    existing?.id !== null
  ) {
    merged.id = existing.id;
  }
  if (!merged.name?.trim() && existing?.name?.trim()) {
    merged.name = existing.name;
  }

  const enriched = enrichStationRecord(merged);
  const serialized = JSON.stringify(enriched);
  localStorage.setItem("stationData", serialized);
  localStorage.setItem("selectedStation", serialized);
  return enriched;
}

export function clearFlowSessionLocalStorage(): void {
  localStorage.removeItem("selectedAiSelfieArt");
  clearAiSelfieCaricatureHobbyPrompt();
  clearAiSelfieCaricatureHobbyPostPayment();
  localStorage.removeItem(SELECTED_CARICATURE_HOBBIES_STORAGE_KEY);
  localStorage.removeItem(HOBBY_GENERATION_STORAGE_KEY);
  clearHobbyCharacterNologoMap();
  localStorage.removeItem(SELECTED_FIFA_CARD_DESIGN_KEY);
  localStorage.removeItem(SELECTED_FIFA_CATEGORY_STORAGE_KEY);
  localStorage.removeItem(SELECTED_FIFA_TEAM_STORAGE_KEY);
  localStorage.removeItem(FIFA_PLAYER_PROFILE_KEY);
  localStorage.removeItem(FIFA_PLAYER_STATS_KEY);
  localStorage.removeItem(FIFA_SELFIE_KEY);
  localStorage.removeItem(FIFA_GENERATED_CARD_KEY);
  localStorage.removeItem(FIFA_CARD_SAVE_REQUESTED_KEY);
  localStorage.removeItem(FIFA_CARD_TRY_AGAIN_USED_KEY);
  localStorage.removeItem("transformationCustomLine");
}

export function readSelectedAiSelfieArtFromLocalStorage(): AiSelfieArt | null {
  try {
    const raw = localStorage.getItem("selectedAiSelfieArt");
    return raw ? (JSON.parse(raw) as AiSelfieArt) : null;
  } catch {
    return null;
  }
}

export function persistSelectedAiSelfieArt(art: AiSelfieArt): void {
  localStorage.setItem("selectedAiSelfieArt", JSON.stringify(art));
  if (isCaricatureHobbyAiSelfieArt(art)) {
    clearAiSelfieCaricatureHobbyPrompt();
    clearAiSelfieCaricatureHobbyPostPayment();
    localStorage.removeItem(SELECTED_CARICATURE_HOBBIES_STORAGE_KEY);
    localStorage.removeItem(HOBBY_GENERATION_STORAGE_KEY);
    clearHobbyCharacterNologoMap();
  }
}

export function prepareStationFlowLocalStorage(station: StoredStationRecord): StoredStationRecord {
  clearFlowSessionLocalStorage();
  return persistStationSelection(station);
}

export async function preloadFifaWorldCupSelectedArt(
  station: StoredStationRecord
): Promise<AiSelfieArt | null> {
  if (!isFifaWorldCup2026Station(station)) return null;

  const categoryId = station.id;
  if (categoryId === undefined || categoryId === null || String(categoryId).trim() === "") {
    return null;
  }

  const res = await getActiveAiSelfieArtsByCategory(String(categoryId));
  const firstArt = res.success ? res.data[0] ?? null : null;
  if (firstArt) {
    localStorage.setItem("selectedAiSelfieArt", JSON.stringify(firstArt));
  }
  return firstArt;
}

export function syncIdleStartNowLocalStorage(options: {
  selectedArt: AiSelfieArt | null;
  isCaricatureHobby: boolean;
  isFifaWorldCup2026: boolean;
}): void {
  const { selectedArt, isCaricatureHobby, isFifaWorldCup2026 } = options;

  if (selectedArt) {
    localStorage.setItem("selectedAiSelfieArt", JSON.stringify(selectedArt));
    if (!isCaricatureHobby && !isFifaWorldCup2026) {
      clearAiSelfieCaricatureHobbyPrompt();
      clearAiSelfieCaricatureHobbyPostPayment();
    }
    return;
  }

  if (isFifaWorldCup2026) {
    return;
  }

  localStorage.removeItem("selectedAiSelfieArt");
  clearAiSelfieCaricatureHobbyPrompt();
  clearAiSelfieCaricatureHobbyPostPayment();
}

export function persistSelectedFifaCardDesign(designId: FifaCardDesignId): void {
  localStorage.setItem(SELECTED_FIFA_CARD_DESIGN_KEY, designId);
}

export function readSelectedFifaCardDesign(): FifaCardDesignId | null {
  try {
    const raw = localStorage.getItem(SELECTED_FIFA_CARD_DESIGN_KEY)?.trim();
    return raw || null;
  } catch {
    return null;
  }
}

export function persistFifaPlayerProfile(profile: FifaPlayerProfile): void {
  localStorage.setItem(FIFA_PLAYER_PROFILE_KEY, JSON.stringify(profile));
}

export function readFifaPlayerProfile(): FifaPlayerProfile | null {
  try {
    const raw = localStorage.getItem(FIFA_PLAYER_PROFILE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<FifaPlayerProfile>;
    return {
      clubTeam: typeof parsed.clubTeam === "string" ? parsed.clubTeam : "",
      firstName: typeof parsed.firstName === "string" ? parsed.firstName : "",
      lastName: typeof parsed.lastName === "string" ? parsed.lastName : "",
      jerseyNumber:
        typeof parsed.jerseyNumber === "string" ? parsed.jerseyNumber : "",
    };
  } catch {
    return null;
  }
}

export function clearFifaPlayerProfile(): void {
  localStorage.removeItem(FIFA_PLAYER_PROFILE_KEY);
}

export const DEFAULT_FIFA_PLAYER_STATS: FifaPlayerStats = {
  position: "ST",
  rating: "",
  pac: 80,
  sho: 80,
  pas: 80,
  dri: 80,
  def: 80,
  phy: 80,
};

export function persistFifaPlayerStats(stats: FifaPlayerStats): void {
  localStorage.setItem(FIFA_PLAYER_STATS_KEY, JSON.stringify(stats));
}

export function readFifaPlayerStats(): FifaPlayerStats | null {
  try {
    const raw = localStorage.getItem(FIFA_PLAYER_STATS_KEY);
    return raw ? (JSON.parse(raw) as FifaPlayerStats) : null;
  } catch {
    return null;
  }
}

export function clearFifaPlayerStats(): void {
  localStorage.removeItem(FIFA_PLAYER_STATS_KEY);
}

export function persistFifaSelfie(imageUrl: string): void {
  const trimmed = imageUrl?.trim();
  if (!trimmed) return;
  localStorage.setItem(FIFA_SELFIE_KEY, trimmed);
}

export function readFifaSelfie(): string | null {
  try {
    const raw = localStorage.getItem(FIFA_SELFIE_KEY);
    return raw?.trim() || null;
  } catch {
    return null;
  }
}

export function clearFifaSelfie(): void {
  localStorage.removeItem(FIFA_SELFIE_KEY);
}

export function resolveFifaCapturedImage(options?: {
  locationImage?: string | null;
  reduxImage?: string | null;
}): string | null {
  return (
    options?.locationImage?.trim() ||
    options?.reduxImage?.trim() ||
    readFifaSelfie()
  );
}

export function persistFifaGeneratedCard(cardImageUrl: string): void {
  localStorage.setItem(FIFA_GENERATED_CARD_KEY, cardImageUrl);
}

export function readFifaGeneratedCard(): string | null {
  try {
    const raw = localStorage.getItem(FIFA_GENERATED_CARD_KEY);
    return raw?.trim() || null;
  } catch {
    return null;
  }
}

export function persistFifaCardSaveRequested(requested: boolean): void {
  localStorage.setItem(FIFA_CARD_SAVE_REQUESTED_KEY, requested ? "1" : "0");
}

export function readFifaCardSaveRequested(): boolean {
  return localStorage.getItem(FIFA_CARD_SAVE_REQUESTED_KEY) === "1";
}

export function persistFifaCardTryAgainUsed(used: boolean): void {
  localStorage.setItem(FIFA_CARD_TRY_AGAIN_USED_KEY, used ? "1" : "0");
}

export function readFifaCardTryAgainUsed(): boolean {
  return localStorage.getItem(FIFA_CARD_TRY_AGAIN_USED_KEY) === "1";
}

export function persistTransformationTryAgainUsed(used: boolean): void {
  localStorage.setItem(TRANSFORMATION_TRY_AGAIN_USED_KEY, used ? "1" : "0");
}

export function readTransformationTryAgainUsed(): boolean {
  return localStorage.getItem(TRANSFORMATION_TRY_AGAIN_USED_KEY) === "1";
}

export function clearTransformationTryAgainUsed(): void {
  localStorage.removeItem(TRANSFORMATION_TRY_AGAIN_USED_KEY);
}

const KIOSK_LANDING_PRESERVED_KEYS = [
  "stationData",
  "selectedStation",
  "selectedAiSelfieArt",
] as const;

/** Clear kiosk session data but keep station + selected art keys. */
export function resetKioskLandingLocalStorage(): void {
  const preserved = Object.fromEntries(
    KIOSK_LANDING_PRESERVED_KEYS.map((key) => [key, localStorage.getItem(key)])
  );

  localStorage.clear();

  KIOSK_LANDING_PRESERVED_KEYS.forEach((key) => {
    const value = preserved[key];
    if (value != null) {
      localStorage.setItem(key, value);
    }
  });
}

const POST_PAYMENT_PRESERVED_KEYS = ["stationData", "selectedStation"] as const;

/** After payment: clear everything except station category data. */
export function resetPostPaymentLocalStorage(): void {
  const preserved = Object.fromEntries(
    POST_PAYMENT_PRESERVED_KEYS.map((key) => [key, localStorage.getItem(key)])
  );

  localStorage.clear();

  POST_PAYMENT_PRESERVED_KEYS.forEach((key) => {
    const value = preserved[key];
    if (value != null) {
      localStorage.setItem(key, value);
    }
  });
}

/** Reset persisted Redux + local session when user lands on category / idle screens. */
export function resetKioskLandingAppState(dispatch: AppDispatch): void {
  resetKioskLandingLocalStorage();
  dispatch(resetFlow());
  dispatch(clearCart());
  dispatch(resetQRScanState());

  const station = readActiveStoredStation();
  if (station) {
    dispatch(setPresetStation(station));
  }
}

/** Full session wipe after successful payment — keeps only stationData / selectedStation. */
export function resetPostPaymentAppState(dispatch: AppDispatch): void {
  resetPostPaymentLocalStorage();
  clearPendingPaymentOrder();
  clearStoredAuthUser();
  dispatch(resetFlow());
  dispatch(clearCart());
  dispatch(resetQRScanState());
  dispatch(clearFavourites());

  const station = readActiveStoredStation();
  if (station) {
    dispatch(setPresetStation(station));
  } else {
    dispatch(clearPresetStation());
  }
}

/** Full reset on home — clears stationData, selectedStation, selectedAiSelfieArt, and all session data. */
export function resetHomeAppState(dispatch: AppDispatch): void {
  localStorage.clear();
  clearMerchGenerateCache();
  dispatch(resetFlow());
  dispatch(clearCart());
  dispatch(resetQRScanState());
  dispatch(clearPresetStation());
}
